/**
 * Centralised unit policy.
 *
 * The simulation is entirely SI:
 *
 *   distance    metres            (m)
 *   time        seconds           (s)
 *   mass        kilograms         (kg)
 *   velocity    metres/second     (m/s)
 *   force       newtons           (N)
 *   torque      newton-metres     (N m)
 *   energy      joules            (J)
 *   power       watts             (W)
 *   temperature kelvin            (K)
 *   pressure    pascals           (Pa)
 *
 * Rendering units are NOT physical units. Every scene declares an explicit
 * metres-per-render-unit factor, and conversions go through this module so the
 * mapping is auditable in one place. Nothing should hard-code "1 unit == 1 m".
 */

export const AU = 1.495978707e11; // metres
export const KM = 1000;
export const DAY = 86400; // seconds
export const HOUR = 3600;
export const MINUTE = 60;
export const YEAR = 365.25 * DAY;

/** Newtonian gravitational constant, m^3 kg^-1 s^-2. */
export const G = 6.6743e-11;

/** Standard gravity, m/s^2. Used only for g-force reporting and Isp maths. */
export const g0 = 9.80665;

/** Stefan-Boltzmann constant, W m^-2 K^-4. */
export const SIGMA_SB = 5.670374419e-8;

/** Universal gas constant, J mol^-1 K^-1. */
export const R_GAS = 8.314462618;

export const KELVIN_OFFSET = 273.15;

export function auToMetres(au: number): number {
  return au * AU;
}

export function metresToAu(m: number): number {
  return m / AU;
}

export function kmToMetres(km: number): number {
  return km * KM;
}

export function metresToKm(m: number): number {
  return m / KM;
}

export function kelvinToCelsius(k: number): number {
  return k - KELVIN_OFFSET;
}

export function celsiusToKelvin(c: number): number {
  return c + KELVIN_OFFSET;
}

/** Standard gravitational parameter mu = G*M, m^3/s^2. */
export function mu(massKg: number): number {
  return G * massKg;
}

/** Effective exhaust velocity from specific impulse, m/s. */
export function exhaustVelocity(ispSeconds: number): number {
  return ispSeconds * g0;
}

/**
 * Tsiolkovsky rocket equation.
 * Returns the delta-v available for a burn from `initialMass` to `finalMass`.
 */
export function deltaV(exhaustVelocityMps: number, initialMass: number, finalMass: number): number {
  if (finalMass <= 0 || initialMass <= finalMass) return 0;
  return exhaustVelocityMps * Math.log(initialMass / finalMass);
}

/** Mass flow (kg/s) for a given thrust and exhaust velocity. */
export function massFlow(thrustN: number, exhaustVelocityMps: number): number {
  return exhaustVelocityMps > 0 ? thrustN / exhaustVelocityMps : 0;
}

/**
 * Render-scale descriptor. Each render layer owns one of these so the
 * conversion between simulation metres and Three.js units is explicit.
 */
export class RenderScale {
  constructor(public readonly metresPerUnit: number) {}

  toUnits(metres: number): number {
    return metres / this.metresPerUnit;
  }

  toMetres(units: number): number {
    return units * this.metresPerUnit;
  }
}

/** 1 render unit == 1 metre. Used by the world and near layers. */
export const METRE_SCALE = new RenderScale(1);
